import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from 'flowbite-react';
import { ReplyTemplate } from '../../models';
import { ReplyTemplates } from '../../services';
import { Alert, Button, Confirm, Icon, Wysiwyg } from '..';

export type ReplyTemplatesManagerProps = {
    /** When set, list/create templates for this project (project_only unless includeGlobalInList). */
    projectId?: string | null;
    /** Display name used in the project-scoped title. */
    projectName?: string | null;
    /** Settings / global manager: only globals. */
    globalOnly?: boolean;
    /** Compact mode for composer dropdown manage modal. */
    compact?: boolean;
    /** Optional className on root. */
    className?: string;
};

function ReplyTemplatesManager(props: ReplyTemplatesManagerProps) {
    const { projectId, projectName, globalOnly = false, compact = false, className } = props;
    const { t } = useTranslation();

    const [templates, setTemplates] = useState<ReplyTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [activeId, setActiveId] = useState<string | null>(null);
    const [toDelete, setToDelete] = useState<string | null>(null);
    const [draftName, setDraftName] = useState('');
    const [draftBody, setDraftBody] = useState('');
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params: AnyObj = {};
            if (globalOnly)
                params.global_only = 1;
            else if (projectId) {
                params.project = projectId;
                params.project_only = 1;
            }
            const json = await ReplyTemplates.list(params);
            setTemplates(json.reply_templates || []);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : (t('reply_templates.load_failed') || ''));
        } finally {
            setLoading(false);
        }
    }, [globalOnly, projectId, t]);

    useEffect(() => {
        load();
    }, [load]);

    const openEdit = (template: ReplyTemplate) => {
        if (activeId === template._id) {
            setActiveId(null);
            return;
        }
        setActiveId(template._id);
        setDraftName(template.name);
        setDraftBody(template.body || '');
        setError('');
    };

    const saveActive = async () => {
        if (!activeId)
            return;
        const name = draftName.trim();
        if (!name) {
            setError(t('reply_templates.name_required') || '');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const json = await ReplyTemplates.update({
                _id: activeId,
                name,
                body: draftBody,
                project: globalOnly ? null : (projectId || null),
            }, /*patch*/ false);
            setTemplates(prev => prev.map(item => (
                item._id === activeId ? json.reply_template : item
            )));
            setActiveId(null);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : (t('reply_templates.save_failed') || ''));
        } finally {
            setSaving(false);
        }
    };

    const createTemplate = async () => {
        const name = newName.trim();
        if (!name) {
            setError(t('reply_templates.name_required') || '');
            return;
        }
        setCreating(true);
        setError('');
        try {
            const json = await ReplyTemplates.create({
                name,
                body: `<p>${t('reply_templates.default_body') || ''}</p>`,
                project: globalOnly ? null : (projectId || null),
            });
            setTemplates(prev => [...prev, json.reply_template].sort((a, b) => a.name.localeCompare(b.name)));
            setNewName('');
            openEdit(json.reply_template);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : (t('reply_templates.save_failed') || ''));
        } finally {
            setCreating(false);
        }
    };

    const removeTemplate = async () => {
        if (!toDelete)
            return;
        try {
            await ReplyTemplates.delete(toDelete);
            setTemplates(prev => prev.filter(item => item._id !== toDelete));
            if (activeId === toDelete)
                setActiveId(null);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : (t('reply_templates.delete_failed') || ''));
        } finally {
            setToDelete(null);
        }
    };

    return (
        <div className={`reply-templates-manager ${className || ''}`}>
            {!compact && (
                <>
                    <h2 className="mt-1 mb-1 text-base font-semibold text-lg text-gray-900 dark:text-gray-100">
                        {globalOnly
                            ? t('reply_templates.title_global')
                            : t('reply_templates.title_project', { name: projectName || '…' })}
                    </h2>
                    <p className="mb-3 text-sm leading-6 font-normal text-gray-600 dark:text-gray-300">
                        {globalOnly
                            ? t('reply_templates.desc_global')
                            : t('reply_templates.desc_project')}
                    </p>
                    <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                        {t('reply_templates.variables_help')}
                    </p>
                </>
            )}

            {error && (
                <div className="mb-3">
                    <Alert title={error} icon="warning" color="failure" />
                </div>
            )}

            {loading ? (
                <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Icon type="loading" />
                    <span>{t('common.loading')}</span>
                </div>
            ) : (
                <div className="flex flex-col space-y-3">
                    {templates.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('reply_templates.empty')}</p>
                    )}
                    {templates.map((template: ReplyTemplate) => (
                        <div key={template._id} className="flex flex-col">
                            <div
                                onClick={() => openEdit(template)}
                                className={`cursor-pointer flex justify-between items-center border rounded ${activeId === template._id ? 'rounded-b-none bg-gray-50 dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-900'}`}
                            >
                                <div className="text-gray-900 dark:text-gray-100 p-2 flex flex-row items-center space-x-1">
                                    <Icon type="reply" />
                                    <h3 className="text-sm font-medium">{template.name}</h3>
                                    {template.isGlobal() && !globalOnly && (
                                        <span className="text-xs text-gray-400 ml-1">
                                            ({t('reply_templates.scope_global')})
                                        </span>
                                    )}
                                </div>
                                <Icon type={activeId === template._id ? 'up' : 'down'} className="mr-2" />
                            </div>
                            {activeId === template._id && (
                                <div className="border border-t-0 rounded-b p-3 space-y-3 bg-white dark:bg-gray-800 text-left">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                                            {t('reply_templates.name')}
                                        </label>
                                        <input
                                            type="text"
                                            value={draftName}
                                            onChange={(e) => setDraftName(e.target.value)}
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                                            {t('reply_templates.body')}
                                        </label>
                                        <Wysiwyg
                                            value={draftBody}
                                            onChange={(value: string) => setDraftBody(value)}
                                            rows={6}
                                            minimal
                                            className="text-gray-900 dark:text-gray-100 sm:text-sm"
                                        />
                                    </div>
                                    <div className="flex justify-between gap-2">
                                        <Button
                                            title={t('common.delete') || ''}
                                            icon="delete"
                                            color="danger"
                                            small
                                            onClick={() => setToDelete(template._id)}
                                        />
                                        <div className="flex gap-2">
                                            <Button
                                                title={t('common.cancel') || ''}
                                                color="navigate"
                                                small
                                                onClick={() => setActiveId(null)}
                                            />
                                            <Button
                                                title={t('common.save') || ''}
                                                icon={saving ? 'loading' : 'save'}
                                                color="primary"
                                                small
                                                loading={saving}
                                                onClick={() => saveActive()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    <div className="flex flex-row items-center space-x-0 border bg-blue-100 dark:bg-blue-900/40 rounded">
                        <label className="max-w-[200px] flex-1 flex items-center text-sm px-2">
                            {t('reply_templates.add')}
                        </label>
                        <div className="flex-1">
                            <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter')
                                        createTemplate();
                                }}
                                className="block w-full border-0 bg-transparent py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-0"
                                placeholder={t('reply_templates.name_placeholder') || ''}
                            />
                        </div>
                        <Button
                            title=""
                            icon={creating ? 'loading' : 'plus'}
                            color="primary"
                            small
                            loading={creating}
                            onClick={() => createTemplate()}
                            className="m-1"
                        />
                    </div>
                </div>
            )}

            <Confirm
                visible={!!toDelete}
                onCancel={() => setToDelete(null)}
                onConfirm={removeTemplate}
                title={t('reply_templates.confirm_delete') || ''}
                iconType="warning"
            />
        </div>
    );
}

export type ReplyTemplatesManageModalProps = {
    show: boolean;
    onClose: () => void;
    projectId?: string | null;
    projectName?: string | null;
};

function ReplyTemplatesManageModal(props: ReplyTemplatesManageModalProps) {
    const { show, onClose, projectId, projectName } = props;
    const { t } = useTranslation();

    const modal = (
        <Modal
            show={show}
            onClose={onClose}
            size="3xl"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
            <Modal.Header>
                {t('reply_templates.manage_title')}
            </Modal.Header>
            <Modal.Body>
                <div className="space-y-6">
                    <ReplyTemplatesManager globalOnly compact={false} />
                    {projectId && (
                        <div className="border-t pt-4">
                            <ReplyTemplatesManager
                                projectId={projectId}
                                projectName={projectName}
                                compact={false}
                            />
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button title={t('common.close') || t('common.cancel') || ''} color="navigate" onClick={onClose} />
            </Modal.Footer>
        </Modal>
    );

    return ReactDOM.createPortal(modal, document.body);
}

export { ReplyTemplatesManager, ReplyTemplatesManageModal };
export default ReplyTemplatesManager;
